/**
 * Cercle — Le Cercle : réseau social communautaire transversal aux orgs.
 *
 * V23.15.68 — Feed vertical de posts, composer, réactions emoji, commentaires,
 * signalement, filtre par type/période, "mes posts".
 *
 * Route : /app/cercle (remplace /app/bouteille qui redirige)
 */
import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { notify, fmtError } from "@/lib/notify";
import { useTranslation } from "react-i18next";
import {
  Loader2, Send, MessageCircle, Flag, Trash2, Image as ImageIcon, Link as LinkIcon,
  Lock,
  Youtube, X, Plus, Filter, TrendingUp, Sparkles, Users2, HelpCircle, Megaphone, Banknote, HandCoins, EyeOff,
} from "lucide-react";

const POST_TYPES = [
  { slug: "capital",           label: "Capital",              icon: Banknote,   accent: "#1D9E75" },
  { slug: "bridge_loan",       label: "Bridge Loan",          icon: HandCoins,  accent: "#EF9F27" },
  { slug: "opportunite",       label: "Opportunité",          icon: TrendingUp, accent: "#378ADD" },
  { slug: "expertise_cherche", label: "Cherche expertise",    icon: HelpCircle, accent: "#7F77DD" },
  { slug: "expertise_offre",   label: "Offre expertise",      icon: Sparkles,   accent: "#7F77DD" },
  { slug: "question",          label: "Question",             icon: HelpCircle, accent: "#71717A" },
  { slug: "annonce",           label: "Annonce",              icon: Megaphone,  accent: "#E24B4A" },
];
const REACTIONS = ["🔥", "💡", "🤝", "👀", "💬"];

function typeMeta(slug) {
  return POST_TYPES.find((t) => t.slug === slug) || POST_TYPES[POST_TYPES.length - 1];
}

function fmtWhen(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 60000;
  if (diff < 1) return "à l'instant";
  if (diff < 60) return `il y a ${Math.round(diff)} min`;
  if (diff < 60 * 24) return `il y a ${Math.round(diff / 60)} h`;
  return d.toLocaleDateString("fr-FR", { day: "numeric", month: "short" });
}

export default function Cercle() {
  const { t } = useTranslation();
  const [posts, setPosts] = useState([]);
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState("");
  const [filterPeriod, setFilterPeriod] = useState("");
  const [mine, setMine] = useState(false);
  const [composerOpen, setComposerOpen] = useState(false);
  const [openPost, setOpenPost] = useState(null);
  const [inboxOpen, setInboxOpen] = useState(false);
  const [inbox, setInbox] = useState({ received: [], sent: [], unread: 0 });

  const loadInbox = useCallback(async () => {
    try {
      const r = await api.get("/workspace/cercle/contacts");
      setInbox(r.data || { received: [], sent: [], unread: 0 });
    } catch { /* silent */ }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterType) params.set("type", filterType);
      if (filterPeriod) params.set("period", filterPeriod);
      if (mine) params.set("mine", "true");
      const r = await api.get(`/workspace/cercle/posts?${params.toString()}`);
      setPosts(r.data.items || []);
      setMe(r.data.me);
    } catch (e) {
      notify.error(fmtError(e));
    } finally {
      setLoading(false);
    }
  }, [filterType, filterPeriod, mine]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadInbox(); }, [loadInbox]);

  return (
    <div className="max-w-3xl mx-auto space-y-4 py-4 px-3" data-testid="cercle-page">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#EF9F27] font-semibold">{t("cercle.overline", { defaultValue: "Le Cercle" })}</div>
          <h1 className="text-2xl font-bold text-white mt-0.5">{t("cercle.title", { defaultValue: "Ce que les autres dirigeants cherchent, offrent, ou annoncent." })}</h1>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setInboxOpen(true)}
            className="relative btn-secondary text-xs inline-flex items-center gap-1.5"
            data-testid="cercle-inbox-btn"
            title="Demandes de contact privées"
          >
            <Lock className="w-3.5 h-3.5" /> Messages privés
            {inbox.unread > 0 && (
              <span className="absolute -top-1.5 -right-1.5 min-w-4 h-4 px-1 rounded-full bg-[#E24B4A] text-white text-[9px] font-bold flex items-center justify-center" data-testid="cercle-inbox-unread">
                {inbox.unread}
              </span>
            )}
          </button>
          <button
            onClick={() => setComposerOpen(true)}
            className="btn-primary text-xs inline-flex items-center gap-1.5"
            data-testid="cercle-new-post-btn"
          >
            <Plus className="w-3.5 h-3.5" /> {t("cercle.publish", { defaultValue: "Publier" })}
          </button>
        </div>
      </div>

      {/* Filtres */}
      <div className="flex flex-wrap items-center gap-1.5 text-[11px]" data-testid="cercle-filters">
        <span className="text-zinc-500 inline-flex items-center gap-1"><Filter className="w-3 h-3" /> Type :</span>
        <button
          onClick={() => setFilterType("")}
          className={`px-2 py-0.5 rounded border ${!filterType ? "border-[#EF9F27] bg-[#EF9F27]/15 text-[#EF9F27]" : "border-white/10 text-zinc-400 hover:border-white/25"}`}
          data-testid="cercle-filter-all"
        >Tous</button>
        {POST_TYPES.map((tt) => (
          <button
            key={tt.slug}
            onClick={() => setFilterType(tt.slug === filterType ? "" : tt.slug)}
            className={`px-2 py-0.5 rounded border ${filterType === tt.slug ? "border-transparent text-black font-semibold" : "border-white/10 text-zinc-400 hover:border-white/25"}`}
            style={filterType === tt.slug ? { background: tt.accent } : {}}
            data-testid={`cercle-filter-${tt.slug}`}
          >{tt.label}</button>
        ))}
        <span className="mx-1.5 text-zinc-700">·</span>
        {["7d", "30d", ""].map((p) => (
          <button
            key={p || "all"}
            onClick={() => setFilterPeriod(p)}
            className={`px-2 py-0.5 rounded border ${filterPeriod === p ? "border-[#378ADD] bg-[#378ADD]/15 text-[#378ADD]" : "border-white/10 text-zinc-400"}`}
            data-testid={`cercle-period-${p || "all"}`}
          >{p === "7d" ? "7 jours" : p === "30d" ? "30 jours" : "Tout"}</button>
        ))}
        <span className="mx-1.5 text-zinc-700">·</span>
        <label className="inline-flex items-center gap-1 text-zinc-400 cursor-pointer">
          <input type="checkbox" checked={mine} onChange={(e) => setMine(e.target.checked)} className="accent-[#EF9F27]" data-testid="cercle-filter-mine" />
          Mes posts
        </label>
      </div>

      {/* Feed */}
      {loading ? (
        <div className="text-center text-zinc-500 text-sm py-8 flex items-center justify-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Chargement…</div>
      ) : posts.length === 0 ? (
        <div className="text-center text-zinc-500 text-sm py-16" data-testid="cercle-empty">
          <MessageCircle className="w-8 h-8 mx-auto opacity-30 mb-3" />
          <div>Aucun post pour ces filtres. Soyez le premier à lancer une conversation.</div>
        </div>
      ) : (
        <div className="space-y-3">
          {posts.map((p) => (
            <PostCard key={p.id} post={p} onOpen={() => setOpenPost(p)} onReact={load} onDelete={load} />
          ))}
        </div>
      )}

      {composerOpen && (
        <ComposerModal
          onClose={() => setComposerOpen(false)}
          onCreated={() => { setComposerOpen(false); load(); }}
        />
      )}
      {openPost && (
        <PostDetailModal
          postId={openPost.id}
          onClose={() => { setOpenPost(null); load(); }}
        />
      )}
      {inboxOpen && (
        <InboxModal
          inbox={inbox}
          onClose={() => setInboxOpen(false)}
          onSeen={loadInbox}
        />
      )}
    </div>
  );
}

function InboxModal({ inbox, onClose, onSeen }) {
  const [tab, setTab] = useState("received");
  const list = tab === "received" ? (inbox.received || []) : (inbox.sent || []);
  return (
    <div className="fixed inset-0 z-[90] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="cercle-inbox-modal">
      <div className="bg-zinc-950 border border-white/10 rounded-xl w-full max-w-lg p-5 space-y-3 max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-white inline-flex items-center gap-2"><Lock className="w-4 h-4 text-[#378ADD]" /> Messages privés</h3>
          <button onClick={onClose} className="text-zinc-500 hover:text-white" data-testid="cercle-inbox-close"><X className="w-4 h-4" /></button>
        </div>
        <div className="flex gap-1.5 text-[11px]">
          <button onClick={() => setTab("received")} className={`px-2.5 py-1 rounded border ${tab === "received" ? "border-[#378ADD] bg-[#378ADD]/15 text-[#378ADD]" : "border-white/10 text-zinc-400"}`} data-testid="cercle-inbox-tab-received">Reçues ({inbox.received?.length || 0})</button>
          <button onClick={() => setTab("sent")} className={`px-2.5 py-1 rounded border ${tab === "sent" ? "border-[#378ADD] bg-[#378ADD]/15 text-[#378ADD]" : "border-white/10 text-zinc-400"}`} data-testid="cercle-inbox-tab-sent">Envoyées ({inbox.sent?.length || 0})</button>
        </div>
        {list.length === 0 ? (
          <div className="text-center text-zinc-500 text-xs py-10">Aucune demande {tab === "received" ? "reçue" : "envoyée"}.</div>
        ) : (
          <div className="space-y-2">
            {list.map((c) => (
              <div key={c.id} className="rounded-lg border border-white/10 bg-white/[0.02] p-3" data-testid={`cercle-contact-item-${c.id}`}>
                <div className="text-[11px] text-zinc-500 mb-1">
                  À propos de : <span className="text-zinc-300">« {c.post_title} »</span>
                </div>
                {tab === "received" && (
                  <div className="text-xs text-white font-medium">{c.from_name} · <span className="text-zinc-400 font-normal">{c.from_org}</span></div>
                )}
                <p className="text-sm text-zinc-300 mt-1 leading-relaxed">{c.message}</p>
                {tab === "received" && c.from_email && (
                  <a href={`mailto:${c.from_email}`} className="inline-flex items-center gap-1 text-[11px] text-[#EF9F27] hover:underline mt-2" data-testid={`cercle-contact-reply-${c.id}`}>
                    <Send className="w-3 h-3" /> Répondre par email ({c.from_email})
                  </a>
                )}
                <div className="text-[10px] text-zinc-600 mt-1.5">{fmtWhen(c.created_at)}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function PostCard({ post, onOpen, onReact, onDelete }) {
  const meta = typeMeta(post.type);
  const Icon = meta.icon;
  const [contactOpen, setContactOpen] = useState(false);
  const del = async (e) => {
    e.stopPropagation();
    if (!window.confirm("Supprimer ce post ?")) return;
    try { await api.delete(`/workspace/cercle/posts/${post.id}`); onDelete?.(); notify.success("Post supprimé."); }
    catch (err) { notify.error(fmtError(err)); }
  };
  const doReact = async (e, emoji) => {
    e.stopPropagation();
    try { await api.post(`/workspace/cercle/posts/${post.id}/react`, { emoji }); onReact?.(); }
    catch (err) { notify.error(fmtError(err)); }
  };
  return (
    <div
      className="noam-card p-4 space-y-3 cursor-pointer hover:border-white/20 transition-colors"
      onClick={onOpen}
      data-testid={`cercle-post-${post.id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: `${meta.accent}18`, border: `1px solid ${meta.accent}55` }}>
            <Icon className="w-3.5 h-3.5" style={{ color: meta.accent }} />
          </div>
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-wider font-semibold flex items-center gap-1.5" style={{ color: meta.accent }}>
              {meta.label}
              {post.is_anonymous && (
                <span className="inline-flex items-center gap-0.5 text-[9px] text-zinc-400 border border-white/15 rounded px-1 py-0.5" data-testid={`cercle-post-anon-badge-${post.id}`}>
                  <EyeOff className="w-2.5 h-2.5" /> Anonyme
                </span>
              )}
            </div>
            <div className="text-[11px] text-zinc-500">
              <span className="text-zinc-300">
                {post.anonymous_note === "owner"
                  ? `${post.author_name} (vous · anonyme)`
                  : post.anonymous_note === "admin"
                  ? `${post.author_name} · identité visible (modération)`
                  : post.author_name}
              </span>
              <span className="mx-1">·</span>
              {post.org_name}
              <span className="mx-1">·</span>
              {fmtWhen(post.created_at)}
            </div>
          </div>
        </div>
        {post.can_delete && (
          <button onClick={del} className="text-zinc-500 hover:text-[#E24B4A] p-1" data-testid={`cercle-post-delete-${post.id}`}>
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
      <div>
        <div className="text-[15px] font-semibold text-white leading-snug">{post.title}</div>
        {post.body && <p className="text-[13px] text-zinc-300 mt-1 leading-relaxed line-clamp-3">{post.body}</p>}
      </div>
      {post.image_url && (
        <img src={post.image_url} alt="" className="w-full max-h-56 object-cover rounded border border-white/5" />
      )}
      {post.video_url && (
        <div className="text-[11px] text-[#378ADD] inline-flex items-center gap-1"><Youtube className="w-3 h-3" /> Vidéo attachée</div>
      )}
      {post.link_url && (
        <a href={post.link_url} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="text-[11px] text-[#378ADD] hover:underline inline-flex items-center gap-1">
          <LinkIcon className="w-3 h-3" /> {(post.link_url || "").slice(0, 60)}
        </a>
      )}
      <div className="flex items-center gap-1 pt-2 border-t border-white/5">
        {REACTIONS.map((emoji) => {
          const count = post.reactions?.[emoji] || 0;
          const mine = post.my_reaction === emoji;
          return (
            <button
              key={emoji}
              onClick={(e) => doReact(e, emoji)}
              className={`text-[13px] px-2 py-0.5 rounded transition ${mine ? "bg-[#EF9F27]/20 border border-[#EF9F27]/50" : "hover:bg-white/[0.06]"}`}
              data-testid={`cercle-react-${post.id}-${emoji}`}
            >
              {emoji} <span className="text-[10px] tabular-nums text-zinc-400">{count || ""}</span>
            </button>
          );
        })}
        {!post.can_delete && (
          <button
            onClick={(e) => { e.stopPropagation(); setContactOpen(true); }}
            className="ml-auto text-[11px] text-[#378ADD] hover:text-[#5AA3EE] inline-flex items-center gap-1"
            data-testid={`cercle-contact-${post.id}`}
            title="Contacter l'auteur en privé"
          >
            <Lock className="w-3 h-3" /> Contacter en privé
          </button>
        )}
        <button
          onClick={onOpen}
          className={`${post.can_delete ? "ml-auto" : ""} text-[11px] text-zinc-400 hover:text-white inline-flex items-center gap-1`}
          data-testid={`cercle-open-${post.id}`}
        >
          <MessageCircle className="w-3 h-3" /> {post.comment_count} commentaire{post.comment_count > 1 ? "s" : ""}
        </button>
      </div>
      {contactOpen && <ContactModal post={post} onClose={() => setContactOpen(false)} />}
    </div>
  );
}

function ContactModal({ post, onClose }) {
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async (e) => {
    e.stopPropagation();
    if (message.trim().length < 5) { notify.error("Message trop court."); return; }
    setBusy(true);
    try {
      await api.post(`/workspace/cercle/posts/${post.id}/contact`, { message: message.trim() });
      notify.success("Demande envoyée à l'auteur. Son identité reste masquée tant qu'il n'accepte pas.");
      onClose();
    } catch (err) { notify.error(fmtError(err, "Envoi impossible.")); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[90] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={(e) => { e.stopPropagation(); onClose(); }} data-testid="cercle-contact-modal">
      <div className="bg-zinc-950 border border-white/10 rounded-xl w-full max-w-md p-5 space-y-3" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2 text-[#378ADD]">
          <Lock className="w-4 h-4" />
          <h3 className="text-sm font-semibold text-white">Contacter en privé</h3>
        </div>
        <p className="text-[11px] text-zinc-400 leading-relaxed">
          À propos de : <span className="text-zinc-200">« {post.title} »</span>.
          {post.is_anonymous && " L'auteur est anonyme — il recevra votre message et votre identité, et décidera de vous répondre. Vous ne verrez son identité que s'il accepte."}
        </p>
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Présentez votre intérêt en quelques lignes…"
          rows={4}
          className="noam-input w-full text-sm"
          data-testid="cercle-contact-message"
        />
        <div className="flex justify-end gap-2">
          <button onClick={onClose} disabled={busy} className="btn-secondary text-xs" data-testid="cercle-contact-cancel">Annuler</button>
          <button onClick={send} disabled={busy} className="btn-primary text-xs" data-testid="cercle-contact-send">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Envoyer
          </button>
        </div>
      </div>
    </div>
  );
}

function ComposerModal({ onClose, onCreated }) {
  const [type, setType] = useState("opportunite");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [videoUrl, setVideoUrl] = useState("");
  const [linkUrl, setLinkUrl] = useState("");
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/workspace/cercle/posts", {
        type, title: title.trim(), body: body.trim(),
        image_url: imageUrl.trim() || null, video_url: videoUrl.trim() || null, link_url: linkUrl.trim() || null,
        is_anonymous: isAnonymous,
      });
      notify.success("Publié dans le Cercle.");
      onCreated?.();
    } catch (e) { notify.error(fmtError(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[110] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => !busy && onClose()} data-testid="cercle-composer-modal">
      <div className="w-full max-w-lg rounded-lg border border-white/10 bg-[#0A0A0F] p-5 space-y-3" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-[#EF9F27] font-semibold">Le Cercle</div>
            <div className="text-[15px] text-white font-semibold mt-0.5">Nouvelle publication</div>
          </div>
          <button onClick={onClose} disabled={busy} className="p-1 hover:bg-white/10 rounded" data-testid="cercle-composer-close"><X className="w-4 h-4 text-zinc-400" /></button>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Type</div>
          <div className="flex flex-wrap gap-1.5">
            {POST_TYPES.map((tt) => (
              <button key={tt.slug} onClick={() => setType(tt.slug)} className={`text-[11px] px-2 py-0.5 rounded border ${type === tt.slug ? "border-transparent text-black font-semibold" : "border-white/10 text-zinc-300 hover:border-white/25"}`} style={type === tt.slug ? { background: tt.accent } : {}} data-testid={`cercle-composer-type-${tt.slug}`}>{tt.label}</button>
            ))}
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Titre (6-120 caractères)</div>
          <input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={120} placeholder="Ex : Recherche co-investisseur seed pour deeptech IA médicale" className="w-full bg-black/40 border border-white/10 rounded px-2 py-1.5 text-[13px] text-white focus:outline-none focus:border-[#EF9F27]" data-testid="cercle-composer-title" />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Corps (max 500)</div>
          <textarea value={body} onChange={(e) => setBody(e.target.value)} maxLength={500} rows={4} placeholder="Détails, contexte, montant, deadline, contact préféré…" className="w-full bg-black/40 border border-white/10 rounded px-2 py-1.5 text-[13px] text-white focus:outline-none focus:border-[#EF9F27]" data-testid="cercle-composer-body" />
          <div className="text-[10px] text-zinc-500 text-right">{body.length}/500</div>
        </div>
        <div className="grid grid-cols-1 gap-2">
          <label className="text-[10px] text-zinc-500 inline-flex items-center gap-1"><LinkIcon className="w-3 h-3" /> Lien (optionnel)</label>
          <input value={linkUrl} onChange={(e) => setLinkUrl(e.target.value)} placeholder="https://..." className="bg-black/40 border border-white/10 rounded px-2 py-1 text-[12px] text-white focus:outline-none focus:border-[#378ADD]" data-testid="cercle-composer-link" />
          <label className="text-[10px] text-zinc-500 inline-flex items-center gap-1 mt-1"><ImageIcon className="w-3 h-3" /> URL image (optionnel)</label>
          <input value={imageUrl} onChange={(e) => setImageUrl(e.target.value)} placeholder="https://..." className="bg-black/40 border border-white/10 rounded px-2 py-1 text-[12px] text-white focus:outline-none focus:border-[#378ADD]" data-testid="cercle-composer-image" />
          <label className="text-[10px] text-zinc-500 inline-flex items-center gap-1 mt-1"><Youtube className="w-3 h-3" /> URL vidéo (YouTube/Vimeo, optionnel)</label>
          <input value={videoUrl} onChange={(e) => setVideoUrl(e.target.value)} placeholder="https://youtube.com/watch?v=..." className="bg-black/40 border border-white/10 rounded px-2 py-1 text-[12px] text-white focus:outline-none focus:border-[#378ADD]" data-testid="cercle-composer-video" />
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <label className="mr-auto inline-flex items-center gap-2 text-[12px] text-zinc-300 cursor-pointer select-none" data-testid="cercle-composer-anon-label">
            <input
              type="checkbox"
              checked={isAnonymous}
              onChange={(e) => setIsAnonymous(e.target.checked)}
              className="accent-[#EF9F27] w-3.5 h-3.5"
              data-testid="cercle-composer-anonymous"
            />
            <EyeOff className="w-3.5 h-3.5 text-zinc-400" />
            Publier anonymement
          </label>
          <button onClick={onClose} disabled={busy} className="btn-secondary text-xs" data-testid="cercle-composer-cancel">Annuler</button>
          <button onClick={submit} disabled={busy || title.trim().length < 6} className="btn-primary text-xs inline-flex items-center gap-1.5" data-testid="cercle-composer-submit">
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
            Publier
          </button>
        </div>
      </div>
    </div>
  );
}

function PostDetailModal({ postId, onClose }) {
  const [data, setData] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const load = useCallback(async () => {
    try {
      const r = await api.get(`/workspace/cercle/posts/${postId}`);
      setData(r.data);
    } catch (e) { notify.error(fmtError(e)); onClose?.(); }
  }, [postId, onClose]);
  useEffect(() => { load(); }, [load]);

  const addComment = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try { await api.post(`/workspace/cercle/posts/${postId}/comments`, { text: text.trim() }); setText(""); await load(); }
    catch (e) { notify.error(fmtError(e)); }
    finally { setBusy(false); }
  };
  const deleteComment = async (cid) => {
    if (!window.confirm("Supprimer ce commentaire ?")) return;
    try { await api.delete(`/workspace/cercle/comments/${cid}`); load(); }
    catch (e) { notify.error(fmtError(e)); }
  };
  const report = async (reason) => {
    try { await api.post(`/workspace/cercle/posts/${postId}/report`, { reason }); notify.success("Post signalé. Merci."); setReportOpen(false); }
    catch (e) { notify.error(fmtError(e)); }
  };
  if (!data) return null;
  const post = data.post;
  const meta = typeMeta(post.type);
  return (
    <div className="fixed inset-0 z-[110] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="cercle-detail-modal">
      <div className="w-full max-w-xl max-h-[85vh] rounded-lg border border-white/10 bg-[#0A0A0F] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="p-4 border-b border-white/10 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: meta.accent }}>{meta.label}</div>
            <div className="text-[15px] text-white font-semibold mt-0.5 truncate">{post.title}</div>
            <div className="text-[10px] text-zinc-500 mt-0.5">{post.author_name} · {post.org_name} · {fmtWhen(post.created_at)}</div>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            <button onClick={() => setReportOpen(true)} className="p-1.5 rounded hover:bg-white/10 text-zinc-500 hover:text-[#EF9F27]" title="Signaler" data-testid="cercle-report-btn"><Flag className="w-3.5 h-3.5" /></button>
            <button onClick={onClose} className="p-1.5 rounded hover:bg-white/10 text-zinc-400" data-testid="cercle-detail-close"><X className="w-4 h-4" /></button>
          </div>
        </div>
        <div className="p-4 overflow-y-auto flex-1 space-y-3">
          {post.body && <p className="text-[13px] text-zinc-200 leading-relaxed whitespace-pre-wrap">{post.body}</p>}
          {post.image_url && <img src={post.image_url} alt="" className="w-full rounded border border-white/5" />}
          {post.link_url && <a href={post.link_url} target="_blank" rel="noopener noreferrer" className="text-[12px] text-[#378ADD] hover:underline inline-flex items-center gap-1"><LinkIcon className="w-3 h-3" /> {post.link_url}</a>}
          {post.video_url && <a href={post.video_url} target="_blank" rel="noopener noreferrer" className="text-[12px] text-[#378ADD] hover:underline inline-flex items-center gap-1"><Youtube className="w-3 h-3" /> {post.video_url}</a>}
          <div className="border-t border-white/5 pt-3 space-y-2">
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold inline-flex items-center gap-1.5"><Users2 className="w-3 h-3" /> {data.comments.length} commentaire{data.comments.length > 1 ? "s" : ""}</div>
            {data.comments.map((c) => (
              <div key={c.id} className="p-2 rounded bg-white/[0.02] border border-white/5" data-testid={`cercle-comment-${c.id}`}>
                <div className="text-[10px] text-zinc-500 flex items-center justify-between">
                  <span><span className="text-zinc-300">{c.author_name}</span> · {c.org_name} · {fmtWhen(c.created_at)}</span>
                  {c.can_delete && <button onClick={() => deleteComment(c.id)} className="text-zinc-600 hover:text-[#E24B4A]"><Trash2 className="w-3 h-3" /></button>}
                </div>
                <div className="text-[12.5px] text-zinc-100 mt-1 whitespace-pre-wrap">{c.text}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="p-3 border-t border-white/10 flex items-center gap-2">
          <input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addComment()} placeholder="Ajouter un commentaire…" className="flex-1 bg-black/40 border border-white/10 rounded px-2 py-1.5 text-[12px] text-white focus:outline-none focus:border-[#EF9F27]" data-testid="cercle-comment-input" />
          <button onClick={addComment} disabled={busy || !text.trim()} className="btn-primary text-xs px-3 py-1.5 inline-flex items-center gap-1" data-testid="cercle-comment-submit">
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
          </button>
        </div>
      </div>
      {reportOpen && (
        <div className="fixed inset-0 z-[120] bg-black/60 flex items-center justify-center p-4" onClick={() => setReportOpen(false)}>
          <div className="w-full max-w-sm rounded-lg border border-white/10 bg-[#0A0A0F] p-4" onClick={(e) => e.stopPropagation()}>
            <div className="text-[13px] text-white font-semibold">Signaler ce post</div>
            <p className="text-[11px] text-zinc-500 mt-1">Un Super Admin vérifiera. Choisis une raison :</p>
            <div className="mt-2 space-y-1">
              {["Spam ou publicité", "Contenu inapproprié", "Fausse annonce / arnaque", "Autre"].map((r) => (
                <button key={r} onClick={() => report(r)} className="w-full text-left text-[12px] px-2 py-1.5 rounded border border-white/10 hover:border-[#E24B4A]/50 text-zinc-200" data-testid={`cercle-report-${r.slice(0,10)}`}>{r}</button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
